import { useState } from 'react';
import {
  Alert, Badge, Button, Group, PasswordInput, Stack, Switch, Text, TextInput,
  Collapse, UnstyledButton, Code, Select,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconShieldLock, IconChevronRight, IconAlertTriangle, IconSend, IconCheck,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { SectionCard } from './SectionCard';
import { formatDateTime } from '../lib/format';

interface Holat {
  enabled: boolean;
  chatId: string | null;
  /** Telegram `getChat` bergan nom — saqlash paytida tekshirilgan. */
  chatTitle: string | null;
  hour: number;
  /** Server hisoblagan daqiqa (jitter) — o'zgartirilmaydi, faqat ko'rsatiladi. */
  minute: number;
  hasToken: boolean;
  lastAt: string | null;
  lastError: string | null;
}

/**
 * ZAXIRA SOZLAMASI — ATAYLAB QIYIN KIRILADIGAN.
 *
 * Bu yerga yiliga bir-ikki marta kiriladi, xato esa qimmatga tushadi: noto'g'ri
 * `chat_id` maktabning butun ma'lumotini BEGONA kanalga yuborib qo'yadi. Shuning
 * uchun odatdagi sozlamalardek ochiq turmaydi:
 *
 *  1) Bo'lim YIG'ILGAN holatda, faqat "ochish" bosilgandan keyin ko'rinadi —
 *     tasodifan aylanib yurib duch kelinmaydi.
 *  2) Ichida qizil ogohlantirish: nima xavf borligi ochiq yozilgan.
 *  3) Tokenni panel QAYTA KO'RSATMAYDI — faqat "kiritilgan/yo'q". O'zgartirish
 *     uchun qaytadan yoziladi.
 *  4) Yoqishdan oldin "sinov yuborish" tavsiya qilinadi: noto'g'ri sozlamani
 *     ertasi kuni emas, o'sha zahoti bilish kerak.
 */
export function BackupSettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [ochiq, setOchiq] = useState(false);
  /* Token kiritilgan bo'lsa maydon YASHIRIN turadi. Doim ochiq tursa, uni
     tasodifan tozalab yoki ustiga yozib yuborish oson bo'lardi — natijada
     zaxira jimgina to'xtardi. Almashtirish ATAYLAB bosiladigan qadam. */
  const [tokenAlmash, setTokenAlmash] = useState(false);

  const { data: holat } = useQuery({
    queryKey: ['backup-settings'],
    queryFn: async () => (await api.get<Holat>('/backup')).data,
    enabled: ochiq, // yopiq turganda so'rov ham yuborilmaydi
  });

  const form = useForm({
    initialValues: { botToken: '', chatId: '' },
    validate: {
      // Bo'sh = tegilmaydi (mavjud token saqlanadi) — shuning uchun shart emas
      botToken: (v) => (v && !/^\d{6,}:[\w-]{30,}$/.test(v.trim()) ? t('backup.tokenBad') : null),
      chatId: (v) => (v && !/^-?\d+$/.test(v.trim()) && !v.trim().startsWith('@') ? t('backup.chatBad') : null),
    },
  });

  const yangila = () => qc.invalidateQueries({ queryKey: ['backup-settings'] });
  const xato = (e: unknown) =>
    notifications.show({
      color: 'red',
      message: (e as { response?: { data?: { error?: string; xabar?: string } } })?.response?.data?.error
        ?? (e as { response?: { data?: { xabar?: string } } })?.response?.data?.xabar
        ?? t('common.error'),
    });

  const saqla = useMutation({
    mutationFn: (v: { botToken?: string; chatId?: string; enabled?: boolean; hour?: number }) => api.put('/backup', v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('common.save') });
      form.setFieldValue('botToken', '');
      setTokenAlmash(false); // saqlandi — maydon yana yashirinadi
      yangila();
    },
    onError: xato,
  });

  const sinov = useMutation({
    mutationFn: () => api.post('/backup/run'),
    onSuccess: (r) => {
      notifications.show({ color: 'green', message: (r.data as { xabar?: string })?.xabar ?? t('backup.sent') });
      yangila();
    },
    onError: xato,
  });

  const ochir = useMutation({
    mutationFn: () => api.delete('/backup'),
    onSuccess: () => { notifications.show({ color: 'green', message: t('backup.removed') }); yangila(); },
    onError: xato,
  });

  return (
    <SectionCard title={t('backup.section')} icon={IconShieldLock} maw={520} mt="md">
      {/* Yig'ilgan holat — bir qatorli, e'tibor tortmaydigan */}
      <UnstyledButton onClick={() => setOchiq((v) => !v)} w="100%">
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">{t('backup.openHint')}</Text>
          <Group gap={6} wrap="nowrap">
            {holat?.enabled && <Badge color="teal" variant="light" size="sm">{t('backup.on')}</Badge>}
            <IconChevronRight
              size={16}
              style={{ transform: ochiq ? 'rotate(90deg)' : undefined, transition: 'transform 150ms' }}
            />
          </Group>
        </Group>
      </UnstyledButton>

      <Collapse in={ochiq}>
        <Stack gap="sm" mt="md">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            {t('backup.warning')}
          </Alert>

          {holat && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">{t('backup.token')}:</Text>
              {holat.hasToken
                ? <Badge color="teal" variant="light" leftSection={<IconCheck size={12} />}>{t('backup.tokenSet')}</Badge>
                : <Badge color="gray" variant="light">{t('backup.tokenNone')}</Badge>}
              {holat.lastAt && (
                <Text size="xs" c="dimmed">{t('backup.lastAt')}: {formatDateTime(holat.lastAt)}</Text>
              )}
            </Group>
          )}

          {holat?.lastError && (
            <Alert color="red" variant="light">{holat.lastError}</Alert>
          )}

          <form onSubmit={form.onSubmit((v) => saqla.mutate({
            botToken: v.botToken.trim() || undefined,
            chatId: v.chatId.trim() || undefined,
          }))}>
            <Stack gap="sm">
              {holat?.hasToken && !tokenAlmash ? (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">{t('backup.tokenConnected')}</Text>
                  <Button size="compact-sm" variant="subtle" onClick={() => setTokenAlmash(true)}>
                    {t('backup.tokenReplace')}
                  </Button>
                </Group>
              ) : (
                <PasswordInput
                  label={t('backup.token')}
                  description={holat?.hasToken ? t('backup.tokenKeep') : t('backup.tokenHelp')}
                  placeholder="123456789:AAE..."
                  autoComplete="off"
                  data-autofocus={tokenAlmash || undefined}
                  {...form.getInputProps('botToken')}
                />
              )}
              <TextInput
                label={t('backup.chatId')}
                description={t('backup.chatHelp')}
                placeholder="-1001234567890"
                defaultValue={holat?.chatId ?? ''}
                {...form.getInputProps('chatId')}
              />
              {/* Tasdiqlangan chat — raqamni ko'z bilan solishtirishdan qutqaradi */}
              {holat?.chatTitle && (
                <Group gap={6} wrap="nowrap">
                  <IconCheck size={15} color="var(--mantine-color-teal-6)" />
                  <Text size="sm">
                    {t('backup.connectedTo')}{' '}
                    <Text span fw={600}>{holat.chatTitle}</Text>
                  </Text>
                </Group>
              )}
              <Group justify="flex-end">
                <Button type="submit" loading={saqla.isPending}>{t('common.save')}</Button>
              </Group>
            </Stack>
          </form>

          <Group align="flex-end" gap="sm">
            <Select
              label={t('backup.hour')}
              description={t('backup.hourHelp')}
              w={150}
              data={Array.from({ length: 24 }, (_, i) => ({
                value: String(i),
                label: `${String(i).padStart(2, '0')}:00`,
              }))}
              value={String(holat?.hour ?? 23)}
              onChange={(v) => v && saqla.mutate({ hour: Number(v) })}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
            />
            {holat && (
              /* Aniq vaqt — foydalanuvchi "nega 23:00 emas?" deb hayron bo'lmasin */
              <Text size="sm" c="dimmed" pb={6}>
                {t('backup.exactAt', {
                  time: `${String(holat.hour).padStart(2, '0')}:${String(holat.minute).padStart(2, '0')}`,
                })}
              </Text>
            )}
          </Group>

          <Group justify="space-between">
            <Switch
              checked={!!holat?.enabled}
              disabled={!holat?.hasToken || !holat?.chatId || saqla.isPending}
              onChange={(e) => saqla.mutate({ enabled: e.currentTarget.checked })}
              label={t('backup.daily')}
              description={t('backup.dailyHint')}
            />
          </Group>

          <Group justify="space-between">
            <Button
              variant="light"
              leftSection={<IconSend size={16} />}
              loading={sinov.isPending}
              disabled={!holat?.hasToken || !holat?.chatId}
              onClick={() => sinov.mutate()}
            >
              {t('backup.testSend')}
            </Button>
            {holat?.hasToken && (
              <Button variant="subtle" color="red" loading={ochir.isPending} onClick={() => ochir.mutate()}>
                {t('backup.remove')}
              </Button>
            )}
          </Group>

          <Text size="xs" c="dimmed">
            {t('backup.scopeNote')} <Code>{'<maktab>-YYYY-MM-DD.json.gz'}</Code>
          </Text>
        </Stack>
      </Collapse>
    </SectionCard>
  );
}
